import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Battle_Action } from "types/battle-engine/battle";
import { BattleReplay } from "@/lib/battle/BattleReplay";
import { ClientPokemon } from "@/lib/battle/entities/client-pokemon";
import { BattleStatsHUD } from "./BattleStatsHUD";
import { CompleteBattleLog } from "types/battle-engine";
import { useDuel } from "@/providers/duel-provider";
import { PokemonBattleHUD } from "@/components/battle/PokemonBattleHUD";
import { KillAnnouncer, KillEvent } from "@/components/battle/KillAnnouncer";

interface BattleScreenProps {
  pokemons: Record<string, ClientPokemon>;
  battleLog: CompleteBattleLog;
  isLocalTest?: boolean;
  onReplayBattle?: () => void;
}

export const BattleScreen: React.FC<BattleScreenProps> = ({
  pokemons,
  battleLog,
  isLocalTest = false,
  onReplayBattle,
}) => {
  const hasAutoPlayed = useRef(false);
  const [currentTick, setCurrentTick] = useState(0);
  const [battleEnded, setBattleEnded] = useState(false);
  const [selectedPokemonId, setSelectedPokemonId] = useState<string | null>(
    null,
  );

  const pokemonIds = useMemo(() => Object.keys(pokemons), [pokemons]);

  // Initial selection
  useEffect(() => {
    if (!selectedPokemonId && pokemonIds.length > 0) {
      setSelectedPokemonId(pokemonIds[0] ?? null);
    }
  }, [pokemonIds, selectedPokemonId]);

  const handleNext = useCallback(() => {
    setSelectedPokemonId((prev) => {
      if (!prev) return pokemonIds[0] ?? null;
      const currentIndex = pokemonIds.indexOf(prev);
      const nextIndex = (currentIndex + 1) % pokemonIds.length;
      return pokemonIds[nextIndex] ?? null;
    });
  }, [pokemonIds]);

  const handlePrevious = useCallback(() => {
    setSelectedPokemonId((prev) => {
      if (!prev) return pokemonIds[pokemonIds.length - 1] ?? null;
      const currentIndex = pokemonIds.indexOf(prev);
      const prevIndex =
        (currentIndex - 1 + pokemonIds.length) % pokemonIds.length;
      return pokemonIds[prevIndex] ?? null;
    });
  }, [pokemonIds]);

  const [killEvents, setKillEvents] = useState<KillEvent[]>([]);
  const { duelState, setDuelState, acknowledgeBattleEnd } = useDuel();

  // Bind click handlers to pokemons
  useEffect(() => {
    Object.values(pokemons).forEach((p) => {
      console.log(`Setting click handler for pokemon ${p.id} (${p.Name})`);
      p.setOnClick((pokemon) => {
        console.log(`Clicked pokemon from handler: ${pokemon.id}`);
        setSelectedPokemonId(pokemon.id.toString());
      });
    });
  }, [pokemons]);

  const playBattle = useCallback(async () => {
    setBattleEnded(false);
    setKillEvents([]);
    const routines: Record<string, Battle_Action[]> = {};

    for (const member of battleLog.teamA.members) {
      routines[member.pokemon.id] = member.actions;
    }
    for (const member of battleLog.teamB.members) {
      routines[member.pokemon.id] = member.actions;
    }

    // Use BattleReplay to execute actions in chronological order with tick updates
    const replay = new BattleReplay(
      pokemons,
      (tick) => {
        setCurrentTick(tick);
      },
      (event) => {
        if (event.event_type === "KILL") {
          const killer = pokemons[event.killer_id];
          const victim = pokemons[event.victim_id];

          if (killer && victim) {
            const newKillEvent: KillEvent = {
              id: `${event.tick}-${event.killer_id}-${event.victim_id}`,
              killerName: killer.Name,
              victimName: victim.Name,
              killerTeam: killer.Team,
              victimTeam: victim.Team,
              killerBaseName: killer.base_pokemon_name,
              victimBaseName: victim.base_pokemon_name,
            };
            setKillEvents((prev) => [...prev, newKillEvent]);
          }
        }
      },
    );

    await replay.play(
      routines,
      battleLog.statsPerTick,
      battleLog.events,
      battleLog.mana,
    );

    console.log("Battle completed", duelState.status);

    // Wait for 2 seconds
    await new Promise((resolve) => setTimeout(resolve, 2000));

    setDuelState((prev) => ({
      ...prev,
      status: "battle_completed",
    }));

    setBattleEnded(true);

    // Skip auto-acknowledge in local test mode to allow replay
    if (isLocalTest) return;

    // Wait for 3 seconds
    await new Promise((resolve) => setTimeout(resolve, 3000));

    acknowledgeBattleEnd();
  }, [pokemons, battleLog, isLocalTest]);

  // Auto-play battle when screen loads
  useEffect(() => {
    if (!hasAutoPlayed.current) {
      hasAutoPlayed.current = true;
      playBattle();
    }
  }, [playBattle]);

  return (
    <>
      <KillAnnouncer events={killEvents} />

      {/* Battle Stats HUD */}
      {battleLog.stats && (
        <BattleStatsHUD
          battleState={pokemons}
          battleStats={battleLog.statsPerTick}
          currentTick={currentTick}
        />
      )}

      {/* Selected Pokemon HUD */}
      {selectedPokemonId && pokemons[selectedPokemonId] && (
        <PokemonBattleHUD
          pokemon={pokemons[selectedPokemonId]}
          battleLog={battleLog}
          currentTick={currentTick}
          onClose={() => setSelectedPokemonId(null)}
          onNext={handleNext}
          onPrevious={handlePrevious}
        />
      )}

      {/* Local Test Controls - shown after battle ends */}
      {isLocalTest && battleEnded && (
        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[10000] flex gap-3">
          <button
            onClick={onReplayBattle}
            className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-lg shadow-lg transition-colors cursor-pointer"
          >
            Replay Battle
          </button>
          <button
            onClick={() => window.location.reload()}
            className="px-5 py-2.5 bg-slate-700 hover:bg-slate-600 text-white font-bold rounded-lg shadow-lg transition-colors cursor-pointer"
          >
            Reload Page
          </button>
        </div>
      )}
    </>
  );
};
